/**
 * ImpactAreaExportCharts.jsx
 * Standalone exportable chart components for Impact Area By-Program and Chord diagrams.
 * One pair of components per Impact Area, suitable for chart registry & ExportRenderer.
 *
 * Factory pattern mirrors ImpactAreaLevelCharts.jsx — each wrapper gets
 * filteredResults from useApp(), filters to Principal + Significant for the
 * target IA, then renders the underlying chart with filtered results + colour.
 */

import { useMemo } from 'react';
import { useApp } from '../../../context/AppContext';
import ImpactAreaByProgramChart from './ImpactAreaByProgramChart';
import ImpactAreaCrossProgramChord from './ImpactAreaCrossProgramChord';
import ImpactAreaWorldMap from './ImpactAreaWorldMap';
import ImpactAreaByTypeChart from './ImpactAreaByTypeChart';
import ImpactAreaFundingChart from './ImpactAreaFundingChart';
import ImpactAreaCrossProgramHeatmap from './ImpactAreaCrossProgramHeatmap';
import ImpactAreaSynergiesChart from './ImpactAreaSynergiesChart';

const IMPACT_AREAS = [
  { key: 'gender',       label: 'Gender Equality',                    field: 'genderLevel',       color: '#009688' },
  { key: 'climate',      label: 'Climate Adaptation & Mitigation',     field: 'climateLevel',      color: '#2196F3' },
  { key: 'nutrition',    label: 'Nutrition, Health & Food Security',   field: 'nutritionLevel',    color: '#FF9800' },
  { key: 'biodiversity', label: 'Environmental Health & Biodiversity', field: 'biodiversityLevel', color: '#4CAF50' },
  { key: 'poverty',      label: 'Poverty Reduction',                   field: 'povertyLevel',      color: '#9C27B0' },
];

/**
 * Filter results to Principal + Significant for a given IA field.
 */
function filterIaResults(results, field) {
  return results.filter(r => {
    const level = r[field];
    return level === 'Principal' || level === 'Significant';
  });
}

/** Factory: creates a standalone By-Program chart component for a given IA. */
function createIaByProgramChart(iaKey) {
  const ia = IMPACT_AREAS.find(a => a.key === iaKey);

  return function IaByProgramExportChart() {
    const { filteredResults } = useApp();

    const iaResults = useMemo(() => {
      if (!ia) return [];
      return filterIaResults(filteredResults, ia.field);
    }, [filteredResults]);

    if (!ia) return null;
    return <ImpactAreaByProgramChart results={iaResults} color={ia.color} />;
  };
}

/** Factory: creates a standalone Chord diagram component for a given IA. */
function createIaChordChart(iaKey) {
  const ia = IMPACT_AREAS.find(a => a.key === iaKey);

  return function IaChordExportChart() {
    const { filteredResults } = useApp();

    const iaResults = useMemo(() => {
      if (!ia) return [];
      return filterIaResults(filteredResults, ia.field);
    }, [filteredResults]);

    if (!ia) return null;
    return <ImpactAreaCrossProgramChord results={iaResults} color={ia.color} />;
  };
}

// By Program (5)
export const GenderByProgramChart       = createIaByProgramChart('gender');
export const ClimateByProgramChart      = createIaByProgramChart('climate');
export const NutritionByProgramChart    = createIaByProgramChart('nutrition');
export const BiodiversityByProgramChart = createIaByProgramChart('biodiversity');
export const PovertyByProgramChart      = createIaByProgramChart('poverty');

// Chord (5)
export const GenderChordChart       = createIaChordChart('gender');
export const ClimateChordChart      = createIaChordChart('climate');
export const NutritionChordChart    = createIaChordChart('nutrition');
export const BiodiversityChordChart = createIaChordChart('biodiversity');
export const PovertyChordChart      = createIaChordChart('poverty');

/** Factory: creates a standalone Geographic Map component for a given IA. */
function createIaGeoMapChart(iaKey) {
  const ia = IMPACT_AREAS.find(a => a.key === iaKey);

  return function IaGeoMapExportChart() {
    const { filteredResults } = useApp();

    const iaResults = useMemo(() => {
      if (!ia) return [];
      return filterIaResults(filteredResults, ia.field);
    }, [filteredResults]);

    if (!ia) return null;
    return <ImpactAreaWorldMap results={iaResults} color={ia.color} label={ia.label} />;
  };
}

/** Factory: creates a standalone By-Type chart component for a given IA. */
function createIaByTypeChart(iaKey) {
  const ia = IMPACT_AREAS.find(a => a.key === iaKey);

  return function IaByTypeExportChart() {
    const { filteredResults } = useApp();

    const iaResults = useMemo(() => {
      if (!ia) return [];
      return filterIaResults(filteredResults, ia.field);
    }, [filteredResults]);

    if (!ia) return null;
    return <ImpactAreaByTypeChart results={iaResults} color={ia.color} />;
  };
}

/** Factory: creates a standalone Funding Split chart component for a given IA. */
function createIaFundingChart(iaKey) {
  const ia = IMPACT_AREAS.find(a => a.key === iaKey);

  return function IaFundingExportChart() {
    const { filteredResults } = useApp();

    const iaResults = useMemo(() => {
      if (!ia) return [];
      return filterIaResults(filteredResults, ia.field);
    }, [filteredResults]);

    if (!ia) return null;
    return <ImpactAreaFundingChart results={iaResults} color={ia.color} />;
  };
}

/** Factory: creates a standalone Cross-Program Heatmap component for a given IA. */
function createIaHeatmapChart(iaKey) {
  const ia = IMPACT_AREAS.find(a => a.key === iaKey);

  return function IaHeatmapExportChart() {
    const { filteredResults } = useApp();

    const iaResults = useMemo(() => {
      if (!ia) return [];
      return filterIaResults(filteredResults, ia.field);
    }, [filteredResults]);

    if (!ia) return null;
    return <ImpactAreaCrossProgramHeatmap results={iaResults} color={ia.color} />;
  };
}

/** Factory: creates a standalone Impact Area Synergies chart for a given IA. */
function createIaSynergiesChart(iaKey) {
  return function IaSynergiesExportChart() {
    const { filteredResults } = useApp();

    return <ImpactAreaSynergiesChart results={filteredResults} activeArea={iaKey} />;
  };
}

// Geographic Map (5)
export const GenderGeoMapChart       = createIaGeoMapChart('gender');
export const ClimateGeoMapChart      = createIaGeoMapChart('climate');
export const NutritionGeoMapChart    = createIaGeoMapChart('nutrition');
export const BiodiversityGeoMapChart = createIaGeoMapChart('biodiversity');
export const PovertyGeoMapChart      = createIaGeoMapChart('poverty');

// By Type (5)
export const GenderByTypeChart       = createIaByTypeChart('gender');
export const ClimateByTypeChart      = createIaByTypeChart('climate');
export const NutritionByTypeChart    = createIaByTypeChart('nutrition');
export const BiodiversityByTypeChart = createIaByTypeChart('biodiversity');
export const PovertyByTypeChart      = createIaByTypeChart('poverty');

// Funding Split (5)
export const GenderFundingChart       = createIaFundingChart('gender');
export const ClimateFundingChart      = createIaFundingChart('climate');
export const NutritionFundingChart    = createIaFundingChart('nutrition');
export const BiodiversityFundingChart = createIaFundingChart('biodiversity');
export const PovertyFundingChart      = createIaFundingChart('poverty');

// Cross-Program Heatmap (5)
export const GenderHeatmapChart       = createIaHeatmapChart('gender');
export const ClimateHeatmapChart      = createIaHeatmapChart('climate');
export const NutritionHeatmapChart    = createIaHeatmapChart('nutrition');
export const BiodiversityHeatmapChart = createIaHeatmapChart('biodiversity');
export const PovertyHeatmapChart      = createIaHeatmapChart('poverty');

// IA Synergies (5)
export const GenderSynergiesChart       = createIaSynergiesChart('gender');
export const ClimateSynergiesChart      = createIaSynergiesChart('climate');
export const NutritionSynergiesChart    = createIaSynergiesChart('nutrition');
export const BiodiversitySynergiesChart = createIaSynergiesChart('biodiversity');
export const PovertySynergiesChart      = createIaSynergiesChart('poverty');
